import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Params, Router, UrlSerializer, UrlTree } from '@angular/router';

import { Observable, of } from 'rxjs';
import { filter, map, switchMap, take, takeUntil } from 'rxjs/operators';

import { BaseContainer } from '@stylehaven/components';
import { AuthenticationSandbox } from '../../sandbox/authentication.sandbox';

@Component({
    selector: 'med-handle-login',
    templateUrl: './handle-login-container.component.html'
})
export class HandleLoginContainerComponent extends BaseContainer implements OnInit, OnDestroy {
    // Application will get stuck if oauth redirects to one of these
    private readonly entryNavBlackList: string[] = ['token', 'auth/redirect'];

    constructor(
        private readonly router: Router,
        private readonly activatedRoute: ActivatedRoute,
        private readonly sandbox: AuthenticationSandbox,
        private urlSerializer: UrlSerializer
    ) {
        super();
    }

    ngOnInit(): void {
        this.handleOAuthToken();
    }

    ngOnDestroy(): void {
        this.sandbox.unsetOauthRedirect();
        super.ngOnDestroy();
    }

    private handleOAuthToken(): void {
        this.activatedRoute.queryParams
            .pipe(
                take(1),
                map(result => {
                    if (result.error) {
                        throw new Error(result.error_description || result.error);
                    }
                    return result;
                }),
                filter((params: Params) => {
                    if (params.code === undefined || params.state === undefined) {
                        this.navigateToHome();
                        return false;
                    }

                    return true;
                }),
                switchMap((params: Params) => this.sandbox.oauth(params.code))
            )
            .subscribe(
                (tokens: boolean) => {
                    if (tokens) {
                        this.navigateToHome();
                    } else {
                        this.navigateToLogin();
                    }
                },
                err => {
                    console.error(err);
                    this.navigateToLogout();
                }
            );
    }

    private navigateToLogout(): void {
        this.router.navigate(['/logout']);
    }

    private getEntryNavigationUrl(): Observable<string | null> {
        const stateKey: string = this.activatedRoute.snapshot.queryParams.state;
        return stateKey ? this.sandbox.getStoredEntryNavigationUrl(stateKey) : of(null);
    }

    private navigateToHome(): void {
        this.navigateToEntryPointOrDefault('/');
    }

    private navigateToLogin(): void {
        this.navigateToEntryPointOrDefault('/login');
    }

    private navigateToEntryPointOrDefault(defaultRoute: string): void {
        this.getEntryNavigationUrl()
            .pipe(take(1), takeUntil(this.destroy$))
            .subscribe(entryNavigationUrl => {
                const safeUrl: string =
                    !!entryNavigationUrl && !this.entryNavBlackList.includes(entryNavigationUrl)
                        ? entryNavigationUrl
                        : defaultRoute;
                const tree: UrlTree = this.urlSerializer.parse(safeUrl);
                this.router.navigateByUrl(tree);
            });
    }
}
